import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { UniversalLink } from '@plone/volto/components';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

const MIME_LABEL = {
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.oasis.opendocument.text': 'ODT',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.oasis.opendocument.spreadsheet': 'ODS',
  'text/csv': 'CSV',
  'application/vnd.ms-powerpoint': 'PPT',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
  'application/zip': 'ZIP',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
};

const TIPOS = ['File', 'Image'];
const LIMITE_PADRAO = 400;
const POR_PAGINA_PADRAO = 25;

const fmtFromMime = (mime, id) => {
  if (mime && MIME_LABEL[mime]) return MIME_LABEL[mime];
  const ext = (id || '').toLowerCase().split('.').pop();
  return ext && ext.length <= 4 ? ext.toUpperCase() : 'Arquivo';
};

// Data da parte literal do ISO (evita shift de timezone). effective ausente
// vira sentinela 1969/1970 → null.
const formatDate = (iso) => {
  if (!iso || iso.startsWith('1969') || iso.startsWith('1970-01-01')) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

// Comparação sem acento nem caixa: quem procura "cotacao" acha "Cotação".
const normaliza = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const AnexosListaView = ({ data }) => {
  // `items` do conteúdo vem em lotes (25 por padrão no Plone), então em páginas
  // com muitos anexos ele chega truncado — os excedentes sumiam da tela sem
  // nenhum aviso. `items_total` diz quantos existem de fato, e é o que permite
  // detectar o corte sem depender do número mágico do tamanho do lote.
  const preloaded = useSelector((s) => s.content?.data?.items || [], shallowEqual);
  const total = useSelector((s) => s.content?.data?.items_total ?? null);
  const path = useSelector((s) =>
    s.content?.data?.['@id'] ? flattenToAppURL(s.content.data['@id']) : '',
  );

  const limit =
    Number.isInteger(data?.limit) && data.limit > 0 ? data.limit : LIMITE_PADRAO;
  const porPagina =
    Number.isInteger(data?.pageSize) && data.pageSize > 0
      ? data.pageSize
      : POR_PAGINA_PADRAO;
  const truncado = total !== null && preloaded.length < total;

  const chaveBusca = `anexos-lista-${path}`;
  const dispatch = useDispatch();

  // Só busca quando a lista pré-carregada está mesmo incompleta. Na maioria das
  // páginas (menos de 25 anexos) nada é requisitado e o comportamento é o de
  // antes — inclusive a renderização no servidor, que continua saindo pronta.
  useEffect(() => {
    if (!path || !truncado) return;
    dispatch(
      searchContent(
        path,
        {
          'path.depth': 1,
          portal_type: TIPOS,
          metadata_fields: ['getObjSize', 'mime_type', 'effective'],
          sort_on: 'getObjPositionInParent',
          b_size: limit,
        },
        chaveBusca,
      ),
    );
  }, [dispatch, path, truncado, limit, chaveBusca]);

  const buscados = useSelector(
    (s) => s.search?.subrequests?.[chaveBusca]?.items || [],
    shallowEqual,
  );

  // Enquanto a busca não volta, mostra o que já veio: a lista nunca fica vazia
  // nem "pisca" — só cresce quando o restante chega.
  const fonte = buscados.length > preloaded.length ? buscados : preloaded;
  const anexos = useMemo(
    () => fonte.filter((it) => TIPOS.includes(it['@type'])).slice(0, limit),
    [fonte, limit],
  );

  const [filtro, setFiltro] = useState('');
  const [pagina, setPagina] = useState(1);

  const filtrados = useMemo(() => {
    const alvo = normaliza(filtro).trim();
    if (!alvo) return anexos;
    return anexos.filter((it) =>
      normaliza(it.title || it['@id'].split('/').pop()).includes(alvo),
    );
  }, [anexos, filtro]);

  const paginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
  // Filtrar encurta a lista; sem isto o leitor ficaria numa página que sumiu.
  const paginaAtual = Math.min(pagina, paginas);
  const visiveis = filtrados.slice(
    (paginaAtual - 1) * porPagina,
    paginaAtual * porPagina,
  );

  if (anexos.length === 0) return null;

  // Filtro e paginação só aparecem quando há o que navegar — em lista curta
  // seriam ruído.
  const mostraNavegacao = anexos.length > porPagina;
  const title = data?.title || 'Anexos';

  return (
    <section className="ag-anexos-lista">
      {/* Título de verdade (h2) fora do cartão; antes era uma faixa azul
          (div) dentro dele. */}
      {title && <h2 className="ag-titulo-secao">{title}</h2>}
      <div className="ag-anexos-lista__panel">
        {mostraNavegacao && (
          <div className="ag-anexos-lista__tools">
            <input
              type="search"
              className="ag-anexos-lista__filtro"
              placeholder="Filtrar por nome…"
              aria-label="Filtrar anexos por nome"
              value={filtro}
              onChange={(e) => {
                setFiltro(e.target.value);
                setPagina(1);
              }}
            />
            <span className="ag-anexos-lista__contagem" aria-live="polite">
              {filtro
                ? `${filtrados.length} de ${anexos.length} documentos`
                : `${anexos.length} documentos`}
            </span>
          </div>
        )}

        {visiveis.length === 0 ? (
          <p className="ag-anexos-lista__vazio">
            Nenhum documento corresponde ao filtro.
          </p>
        ) : (
          <ul className="ag-anexos-lista__list">
            {visiveis.map((it) => {
              const href = it['@id'];
              const label = it.title || href.split('/').pop();
              const meta = [
                fmtFromMime(it.mime_type, href),
                it.getObjSize || null,
                formatDate(it.effective),
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <li key={it['@id']} className="ag-anexos-lista__item">
                  <UniversalLink href={href} className="ag-anexos-lista__link">
                    <span className="ag-anexos-lista__name">{label}</span>
                    {meta && <span className="ag-anexos-lista__meta">{meta}</span>}
                  </UniversalLink>
                </li>
              );
            })}
          </ul>
        )}

        {paginas > 1 && (
          <nav
            className="pagination ag-anexos-lista__pagination"
            aria-label="Paginação dos anexos"
          >
            <button
              type="button"
              className="pagination__btn pagination__btn--icon"
              disabled={paginaAtual <= 1}
              onClick={() => setPagina((p) => Math.max(1, p - 1))}
              aria-label="Página anterior"
            >
              ←
            </button>
            {Array.from({ length: paginas }, (_, i) => i + 1)
              .filter(
                (pg) =>
                  pg === 1 ||
                  pg === paginas ||
                  Math.abs(pg - paginaAtual) <= 2,
              )
              .map((pg, i, arr) => (
                <React.Fragment key={pg}>
                  {i > 0 && arr[i - 1] !== pg - 1 && (
                    <span className="pagination__ellipsis">…</span>
                  )}
                  <button
                    type="button"
                    className={`pagination__btn${
                      pg === paginaAtual ? ' pagination__btn--active' : ''
                    }`}
                    aria-current={pg === paginaAtual ? 'page' : undefined}
                    onClick={() => setPagina(pg)}
                  >
                    {pg}
                  </button>
                </React.Fragment>
              ))}
            <span className="pagination__status">
              Página {paginaAtual} de {paginas}
            </span>
            <button
              type="button"
              className="pagination__btn pagination__btn--icon"
              disabled={paginaAtual >= paginas}
              onClick={() => setPagina((p) => Math.min(paginas, p + 1))}
              aria-label="Próxima página"
            >
              →
            </button>
          </nav>
        )}
      </div>
    </section>
  );
};

AnexosListaView.propTypes = {
  data: PropTypes.shape({
    title: PropTypes.string,
    limit: PropTypes.number,
    pageSize: PropTypes.number,
  }).isRequired,
};

export default AnexosListaView;
